/**
 * Performance › Audits (`/performance/audits`) — the synthetic
 * (Lighthouse) plane, at whichever zoom `?page=` selects: the origin
 * (every audited page) or one page.
 *
 * Promoted from the old `/performance?view=audits` tab to its own route
 * (2026-07-26) so the sidebar section panel can list it; that legacy URL
 * still redirects here via `/performance`'s `beforeLoad`. What were once
 * three sub-tabs are gone: Origin + Pages merged into the one scoped
 * view (`?sub=scores|pages` normalise away), and Third parties — a
 * site-level question none of this view's filters touch — moved out to
 * its own sibling route, which `?sub=thirdparty` now redirects to.
 *
 * Individual audit reports live under `./$auditId`.
 */
import { AUDIT_SCOPE_ORIGIN } from '@/components/audits/AuditMetricCharts';
import { CompareSitePicker, useCompareSites } from '@/components/primitives/CompareSitePicker';
import { DEFAULT_PAGE_RANGE, type PageRange } from '@/components/primitives/PageRangePill';
import { SectionBoundary } from '@/components/primitives/SectionBoundary';
import {
  CHART_METRIC_KEYS,
  type ChartMetricKey,
  LabPerformance,
} from '@/components/rum/PerformanceSection';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import { DEMO_EMBED } from '@/lib/demo';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

interface AuditsSearch {
  /** Selected metric for the origin chart — a Lighthouse category
   *  score or one of the five vitals. Shareable, and the Overview's
   *  audit tiles deep-link into a vital. */
  metric?: ChartMetricKey;
  /** Guest site uuid to compare against (the active site itself means
   *  page-vs-page when the view is scoped to a page). */
  compare?: string;
  /** Scope: a page uuid narrows the view to that page's audit history;
   *  absent = the origin (all audited pages). The deep-link target for
   *  what used to be the `/pages/:pageUuid` detail route. */
  page?: string;
}

const isChartMetricKey = (value: unknown): value is ChartMetricKey =>
  CHART_METRIC_KEYS.includes(value as ChartMetricKey);

export const Route = createFileRoute('/_app/performance/audits/')({
  validateSearch: (search: Record<string, unknown>): AuditsSearch & { sub?: 'thirdparty' } => ({
    // Kept only long enough for `beforeLoad` to redirect it away.
    ...(search.sub === 'thirdparty' ? { sub: 'thirdparty' as const } : {}),
    ...(isChartMetricKey(search.metric) ? { metric: search.metric } : {}),
    ...(typeof search.compare === 'string' && search.compare !== ''
      ? { compare: search.compare }
      : {}),
    ...(typeof search.page === 'string' && search.page !== '' ? { page: search.page } : {}),
  }),
  // Third parties moved to its own route; carry the comparison over.
  beforeLoad: ({ search }) => {
    if (search.sub !== 'thirdparty') return;
    throw redirect({
      to: '/performance/third-parties',
      search: search.compare ? { compare: search.compare } : {},
      replace: true,
    });
  },
  component: AuditsPage,
});

function AuditsPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="Audits" parent={{ label: 'Performance', href: '/performance' }}>
      {siteUuid && <AuditsBody siteUuid={siteUuid} />}
    </SiteIsland>
  );
}

function AuditsBody({ siteUuid }: { siteUuid: string }): ReactElement {
  const { metric, compare, page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [range, setRange] = useState<PageRange>(DEFAULT_PAGE_RANGE);

  // Scope for the audits view: a page uuid, or the origin sentinel.
  const scope = page ?? AUDIT_SCOPE_ORIGIN;
  // `replace` — zooming between the origin and a page is exploration,
  // not navigation, the same call the metric pills make.
  const selectScope = (next: string): void => {
    void navigate({
      search: (prev) => ({
        ...(prev.metric ? { metric: prev.metric } : {}),
        ...(prev.compare ? { compare: prev.compare } : {}),
        ...(next !== AUDIT_SCOPE_ORIGIN ? { page: next } : {}),
      }),
      replace: true,
    });
  };

  // Compare-site resolution. The active site is offered too, pinned
  // first — selecting it turns a page-scoped view into a page-vs-page
  // compare.
  const {
    options: compareOptions,
    compareUuid,
    compareName,
    homeName,
  } = useCompareSites(siteUuid, compare, { includeSelf: true });

  // Re-checked at the point of use, not just `??`-defaulted — a parent
  // route's untouched copy of a bogus `?metric=` can arrive typed as a
  // valid key, and the charts index the metric registry with a `!`.
  const activeMetric: ChartMetricKey = isChartMetricKey(metric) ? metric : 'performance';
  const selectMetric = (next: ChartMetricKey): void => {
    void navigate({ search: (prev) => ({ ...prev, metric: next }), replace: true });
  };

  const comparePicker = (
    <CompareSitePicker
      value={compareUuid}
      options={compareOptions}
      onChange={(next) =>
        void navigate({
          search: (prev) => ({
            ...(prev.metric ? { metric: prev.metric } : {}),
            ...(prev.page ? { page: prev.page } : {}),
            ...(next ? { compare: next } : {}),
          }),
        })
      }
    />
  );

  return (
    <SectionBoundary label="this view">
      <LabPerformance
        siteUuid={siteUuid}
        range={range}
        onRangeChange={setRange}
        metric={activeMetric}
        onMetricChange={selectMetric}
        compareSiteUuid={compareUuid}
        compareName={compareName}
        homeName={homeName}
        pageUuid={scope}
        onPageChange={selectScope}
        actions={DEMO_EMBED ? null : comparePicker}
      />
    </SectionBoundary>
  );
}
